import React, {useEffect, useState} from 'react';
import MainLayout from "../../layouts/main";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {Link, useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import LocationFilter from "../../components/location-filter";
import {useSearchService} from "../../services/search-service";
import {getSearchFilter, setSearchFilter} from "../../modules/auth-persistor";
import {useCategoryService} from "../../services/category-service";
import {useGlobalNotificationMessage} from "../../context/message";
import {AppEvents, AppRoutes, SearchTypes} from "../../utils/constants";
import EventCell from "../../components/event-cell";
import SearchFilter from "../../components/search-filter";
import {useHomepageService} from "../../services/homepage-service";
import ActiveFilters from "../../components/active-filters";
import SearchParamsFilter from "../../components/search-params-filter";
import {useNotifyEventsService} from "../../services/notify-events-service";
import StarRating from "../../components/star-rating";

export default function SearchEvents () {
    const defaultPagination = {limit:20, offset:0};
    const [loaded, setLoaded] = useState(false);
    const [events, setEvents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [localFilter, setLocalFilter] = useState({});
    const [showLoadMore, setShowLoadMore] = useState(false);
    const [showTrainers, setShowTrainers] = useState(false);
    const [trainers, setTrainers] = useState([]);
    const [pagination, setPagination] = useState(defaultPagination);

    const homepageService = useHomepageService();
    const searchService = useSearchService();
    const categoryService = useCategoryService();
    const {addErrorMessage} = useGlobalNotificationMessage();
    const nes = useNotifyEventsService();
    const navigate = useNavigate();
    const location = useLocation();

    const [searchParams] = useSearchParams();
    const pType = searchParams.get('type') || SearchTypes.search;
    const pCategories = searchParams.get('categories');
    const pKeyword = searchParams.get('keyword');
    const pResetFilters = searchParams.get('reset');

    useEffect(() => {
        //on component load we should check type of search, init and adjust filter based on this type and then get the classes.
        let promises = [
            categoryService.getList(),
        ];

        Promise.all(promises).then(async ([categories = []]) => {
            let catGroups = categories?.groups.map(
                (group)=>{
                    group.label = group.name ? group.name : 'Other';
                    group.options = [...group.categories.map((item)=>{
                        item.label = item.name;
                        item.value = item.id;
                        return item;
                    })];
                    return group;
                }
            );

            setCategories(catGroups || []);
            setLoaded(true);
        }).catch(error => {
            HelperService.handleApiErrors(error, addErrorMessage);
        });

        reloadSearch();
    }, []);

    const onFilterChanged = (filter) => {
        reloadSearch(getSearchFilter());
        setLocalFilter(filter);
    }

    useEffect(() => {
        if (loaded){
            reloadSearch();
        }
    }, [pCategories, pKeyword, pResetFilters]);

    function reloadSearch(newFilter=null){
        let promise = null;
        let searchFilter = newFilter || getSearchFilter();
        if (searchFilter){
            //check if we should pre-set the filters
            if (pCategories || pKeyword || pResetFilters){
                if (pResetFilters){
                    searchFilter.categories = null;
                    searchFilter.search = null;
                    searchFilter.datetimeFrom = null;
                    searchFilter.datetimeTo = null;
                    //update url in case of resetparam - so that its onetimeaction
                    const queryParams = new URLSearchParams(location.search);
                    queryParams.delete('reset');
                    navigate({
                        pathname: location.pathname,
                        search: queryParams.toString()
                    }, {replace: true});
                }
                if (pCategories){
                    searchFilter.categories = pCategories;
                }
                if (pKeyword){
                    searchFilter.search = pKeyword;
                }
                //update filter with query params
                setSearchFilter(searchFilter);
                setLocalFilter(searchFilter);

                if (JSON.stringify(localFilter)!==JSON.stringify(searchFilter)){
                    nes.trigger(AppEvents.searchFilterChanged, searchFilter);
                }
            }
            switch (pType){
                case SearchTypes.discover:
                    ///api/v0/discover/categories/WHATS_NEW?datetimeFrom=1716453118761.244&keyword=&lat=48.15965050314217&limit=20&lon=17.10770861041767&radius=45.80977857196515
                    promise = homepageService.discoverClassesByCategories(searchFilter);
                    setShowTrainers(false);
                    break;
                case SearchTypes.popup:
                    ///api/v0/discover/categories/POPUP?datetimeFrom=1716453199423.887&lat=48.15965050314217&limit=20&lon=17.10770861041767&radius=45.80977857196515
                    promise = homepageService.discoverPopupClassesByCategories(searchFilter);
                    setShowTrainers(false);
                    break;
                case SearchTypes.search:
                    ///api/v0/search/Veza?age=20&datetimeFrom=1716372403820.457&daypart=2&difficulty=3&duration=2&lat=48.15965050314217&limit=20&lon=17.10770861041767&offset=0&radius=45.80977857196515&sweatequity=2&sweatmeter=4
                    promise = searchService.search(searchFilter);
                    setShowTrainers(true);
                    break;
                default: break;
            }

            if (promise){
                //get latest events based on filter
                promise.then((res)=>{
                    loadCallback(res);
                }).catch((error)=>{
                    HelperService.handleApiErrors(error, addErrorMessage);
                })
            }
        }
    }

    function loadMoreClasses(){
        let promise = null;
        let searchFilter = getSearchFilter();

        const newPagination = {
            offset: pagination.offset+pagination.limit,
            limit: pagination.limit
        };
        setPagination(newPagination);

        switch (pType){
            case SearchTypes.discover:
                searchFilter.offset = newPagination.offset;
                searchFilter.limit = newPagination.limit;
                ///api/v0/discover/categories/WHATS_NEW?datetimeFrom=1716453118761.244&keyword=&lat=48.15965050314217&limit=20&lon=17.10770861041767&radius=45.80977857196515
                promise = homepageService.discoverClassesByCategories(searchFilter);
                break;
            case SearchTypes.popup:
                searchFilter.offset = newPagination.offset;
                searchFilter.limit = newPagination.limit;
                ///api/v0/discover/categories/POPUP?datetimeFrom=1716453199423.887&lat=48.15965050314217&limit=20&lon=17.10770861041767&radius=45.80977857196515
                promise = homepageService.discoverPopupClassesByCategories(searchFilter);
                break;
            case SearchTypes.search:
                searchFilter.datetimeFrom = events[events.length-1].datetime;
                ///api/v0/search/Veza?age=20&datetimeFrom=1716372403820.457&daypart=2&difficulty=3&duration=2&lat=48.15965050314217&limit=20&lon=17.10770861041767&offset=0&radius=45.80977857196515&sweatequity=2&sweatmeter=4
                promise = searchService.search(searchFilter);
                break;
            default: break;
        }
        if (promise){
            //get latest events based on filter
            promise.then((res)=>{
                loadCallback(res, true);
            }).catch((error)=>{
                HelperService.handleApiErrors(error, addErrorMessage);
            })
        }
    }

    function loadCallback(res, append = false) {
        if (res?.length >= 20) {
            setShowLoadMore(true);
        } else {
            setShowLoadMore(false);
        }

        let newEvents;
        if (append) {
            if (res?.length > 0) {
                let idSet = new Set(events.map(event => event.id));
                res = res.filter(event => !idSet.has(event.id));
            }
            newEvents = [...events, ...res];
        } else {
            newEvents = res?.length > 0 ? [...res] : [];
        }
        setEvents(newEvents);

        const trainerMap = new Map();
        newEvents.forEach(event => {
            if (event.trainer && !trainerMap.has(event.trainer.id)) {
                trainerMap.set(event.trainer.id, event.trainer);
            }
        });
        setTrainers(Array.from(trainerMap.values()));
    }

    const TrainerCell = function ({trainer, ...props}) {
        return ( <Link to={`${AppRoutes.trainers.path}/${trainer.id}`} className="clickable router-link">
            <div className="card px-2 py-3 mb-3 border-0 hover-cell-effect rounded-3">
                <div className="d-flex flex-column  align-items-center gap-3">
                    <div className="rounded-circle bg-cover flex-shrink-0"
                         style={{backgroundImage: `url(${trainer.photo})`, width: '90px', height: '90px'}}></div>
                    <div className="px-2 w-100 text-center" style={{minWidth: 0}}>
                        <h6 className="my-0">{trainer.name}</h6>
                        <h6 className="my-0">{trainer.surname}</h6>
                        <div>

                            <StarRating rating={trainer.rating} count={trainer.reviews} showCount={true} />
                        </div>
                    </div>
                </div>
            </div>
        </Link>);
    }

    return (
        <>
            <Section animation={HelperService.getRandomAnimation()} className="position-relative section-padding-first">
                <div className={"container"}>
                    <div className={"row"}>
                        <div className={"col-sm-12 col-md-6"}>
                            <h1 className="header-stripe-blue">
                                {pType===SearchTypes.discover && <>Discover events</>}
                                {pType===SearchTypes.popup && <>Find Pop-ups</>}
                                {(pType===SearchTypes.search || !pType) && <>Search events</>}
                            </h1>
                            <p>Search for event, class or clinic</p>
                        </div>
                        <div className={"col-sm-12 col-md-6 p-4"}>
                            <div className="row px-2 rounded-3 bg-light">
                                <div className="col-sm-12 col-md-8 col-xl-6 py-2 px-0 d-flex flex-wrap">
                                    <LocationFilter onChange={(filter) => onFilterChanged(filter)}/>
                                    <SearchParamsFilter categoriesList={categories} onChange={(filter) => onFilterChanged(filter)}/>
                                </div>
                                <div className="col-sm-12 col-md-4 col-xl-6 py-2 px-0 flex-shrink-1">
                                    <SearchFilter onChange={(filter) => onFilterChanged(filter)}/>
                                </div>
                                <div className={"col-sm-12 px-0"}>
                                    <ActiveFilters onChange={(filter) => onFilterChanged(filter)}/>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </Section>

            {events?.length > 0 && <>

                {showTrainers && trainers?.length > 0 &&
                    <Section animation={HelperService.getRandomAnimation()} className="position-relative section-padding">
                        <div className={"container"}>
                            <div className={"row"}>
                                {trainers.map(t => <div key={t.id} className={'col-auto mb-2'}>
                                    <TrainerCell trainer={t} />
                                </div>)}
                            </div>
                        </div>
                    </Section>}

                <Section animation={HelperService.getRandomAnimation()} className="position-relative section-padding">
                    <div className={"container"}>
                        <div className={"row"}>
                            {events.map(t => <div key={t.id} className={'col-sm-6 col-md-4 mb-2'}>
                                <EventCell event={t} esid={t.id} link={`${AppRoutes.events.path}/EID`}/>
                            </div>)}
                        </div>
                        {showLoadMore && <div className="text-center">
                            <button className="btn btn-outline-primary my-2" onClick={() => loadMoreClasses()}>Load more events</button>
                        </div>}
                    </div>
                </Section>

            </>}

            {events?.length === 0 && <>

                {pType === SearchTypes.search && localFilter?.search?.length<3 &&
                    <Section animation={HelperService.getRandomAnimation()} className="position-relative section-padding">
                        <div className={"container"}>
                            <div className={"row"}>
                                <h4>Start searching.</h4>
                                <p>You have to write more than 3 letters.</p>
                            </div>
                        </div>
                    </Section>}

                {(pType !== SearchTypes.search || (pType===SearchTypes.search && localFilter?.search?.length>=3)) &&
                    <Section animation={HelperService.getRandomAnimation()} className="position-relative section-padding">
                        <div className={"container"}>
                            <div className={"row"}>
                                <h4>We could not find your fit.</h4>
                                <p>Try changing your filter or location/date settings.</p>
                            </div>
                        </div>
                    </Section>}

            </>}


        </>
    );
}

SearchEvents.getLayout = function getLayout(page) {
    return (
        <MainLayout>
            {page}
        </MainLayout>
    )
}